import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { usersApi } from '../services/api'
import { useNotificationStore } from './notifications'

export const useUserStore = defineStore('users', () => {
  // State
  const users = ref([])
  const currentUser = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const pagination = ref({
    total: 0,
    page: 1,
    size: 20,
    pages: 1
  })
  const filters = ref({
    email: '',
    fullname: '',
    rol_id: null,
    client_id: null,
    include_deleted: false
  })

  // Getters
  const totalUsers = computed(() => pagination.value.total)
  const activeUsers = computed(() =>
    users.value.filter(user => !user.is_deleted).length
  )
  const deletedUsers = computed(() =>
    users.value.filter(user => user.is_deleted).length
  )

  // Actions
  const notificationStore = useNotificationStore()

  const setLoading = (isLoading) => {
    loading.value = isLoading
  }

  const setError = (errorMessage) => {
    error.value = errorMessage
    if (errorMessage) {
      notificationStore.addNotification('error', errorMessage)
    }
  }

  const clearError = () => {
    error.value = null
  }

  const fetchUsers = async (page = 1, size = 20) => {
    setLoading(true)
    clearError()

    try {
      const params = {
        page,
        size,
        ...filters.value
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key]
        }
      })

      const response = await usersApi.getUsers(params)

      users.value = response.items
      pagination.value = {
        total: response.total,
        page: response.page,
        size: response.size,
        pages: response.pages
      }

      return response
    } catch (error) {
      console.error('Error fetching users:', error)
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido al cargar los usuarios'
      setError('No se pudieron cargar los usuarios. ' + errorMsg)

      // Set empty data on error to prevent UI issues
      users.value = []
      pagination.value = {
        total: 0,
        page: 1,
        size: 20,
        pages: 1
      }

      // Don't throw, just return empty response
      return { items: [], total: 0, page: 1, size: 20, pages: 1 }
    } finally {
      setLoading(false)
    }
  }

  const fetchUser = async (id, includeDeleted = false) => {
    setLoading(true)
    clearError()

    try {
      const user = await usersApi.getUser(id, includeDeleted)
      currentUser.value = user
      return user
    } catch (error) {
      setError('Error al cargar el usuario: ' + error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createUser = async (userData) => {
    setLoading(true)
    clearError()

    try {
      const newUser = await usersApi.createUser(userData)
      notificationStore.addNotification('success', `Usuario "${newUser.email}" creado exitosamente`)

      // Refresh the list
      await fetchUsers(pagination.value.page, pagination.value.size)

      return newUser
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al crear el usuario: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateUser = async (id, userData) => {
    setLoading(true)
    clearError()

    try {
      const updatedUser = await usersApi.updateUser(id, userData)
      notificationStore.addNotification('success', `Usuario "${updatedUser.email}" actualizado exitosamente`)

      // Update current user if it's the one being edited
      if (currentUser.value?.id === id) {
        currentUser.value = updatedUser
      }

      // Refresh the list
      await fetchUsers(pagination.value.page, pagination.value.size)

      return updatedUser
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al actualizar el usuario: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteUser = async (id) => {
    setLoading(true)
    clearError()

    try {
      await usersApi.deleteUser(id)
      notificationStore.addNotification('success', 'Usuario eliminado exitosamente')

      // Refresh the list
      await fetchUsers(pagination.value.page, pagination.value.size)
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al eliminar el usuario: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const restoreUser = async (id) => {
    setLoading(true)
    clearError()

    try {
      const restoredUser = await usersApi.restoreUser(id)
      notificationStore.addNotification('success', `Usuario "${restoredUser.email}" restaurado exitosamente`)

      // Refresh the list
      await fetchUsers(pagination.value.page, pagination.value.size)

      return restoredUser
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al restaurar el usuario: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const hardDeleteUser = async (id) => {
    setLoading(true)
    clearError()

    try {
      await usersApi.hardDeleteUser(id)
      notificationStore.addNotification('warning', 'Usuario eliminado permanentemente')

      // Refresh the list
      await fetchUsers(pagination.value.page, pagination.value.size)
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al eliminar permanentemente el usuario: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const generateToken = async (id, expiresDays = 30) => {
    setLoading(true)
    clearError()

    try {
      const tokenResponse = await usersApi.generateToken(id, expiresDays)
      notificationStore.addNotification('success', 'Token API generado exitosamente')

      // Refresh the user to show updated token expiration
      await fetchUser(id)

      return tokenResponse
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al generar el token: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const revokeToken = async (id) => {
    setLoading(true)
    clearError()

    try {
      await usersApi.revokeToken(id)
      notificationStore.addNotification('success', 'Token API revocado exitosamente')

      // Refresh the user to show token revoked
      await fetchUser(id)
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al revocar el token: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const setFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const clearFilters = () => {
    filters.value = {
      email: '',
      fullname: '',
      rol_id: null,
      client_id: null,
      include_deleted: false
    }
  }

  const clearCurrentUser = () => {
    currentUser.value = null
  }

  return {
    // State
    users,
    currentUser,
    loading,
    error,
    pagination,
    filters,

    // Getters
    totalUsers,
    activeUsers,
    deletedUsers,

    // Actions
    fetchUsers,
    fetchUser,
    createUser,
    updateUser,
    deleteUser,
    restoreUser,
    hardDeleteUser,
    generateToken,
    revokeToken,
    setFilters,
    clearFilters,
    clearCurrentUser,
    clearError
  }
})
